import { z } from '@hono/zod-openapi'

export const TemplateEngineSchema = z.enum([
  'handlebars', 'liquid', 'ejs', 'docx-templates',
]).openapi({ example: 'handlebars', description: 'Template rendering engine' })

export const TemplateVariableTypeSchema = z.enum([
  'string', 'number', 'date', 'boolean', 'array', 'object',
]).openapi({ example: 'string' })

export const TemplateVariableSchema = z.object({
  name: z.string().min(1).max(255).openapi({ example: 'student.firstName' }),
  type: TemplateVariableTypeSchema,
  required: z.boolean(),
  defaultValue: z.unknown().nullable(),
  description: z.string().max(1000).openapi({ example: 'The student legal first name' }),
  source: z.string().max(500).nullable().openapi({ example: 'student.firstName', description: 'Domain source path for auto-binding' }),
}).openapi('TemplateVariable', { description: 'A variable declaration for template binding' })

export const ConditionalSectionSchema = z.object({
  name: z.string().min(1).max(255).openapi({ example: 'specialEducationHistory' }),
  condition: z.string().min(1).max(500).openapi({ example: 'student.hasIep === true', description: 'Expression evaluated to determine inclusion' }),
  templateContent: z.string().min(1).openapi({ description: 'Template content rendered when condition is true' }),
  elseContent: z.string().nullable().openapi({ description: 'Template content rendered when condition is false' }),
}).openapi('ConditionalSection', { description: 'A conditional template section included or excluded based on variable evaluation' })

export const TemplateSchema = z.object({
  id: z.string().uuid().openapi({ example: 'tpl-1234-5678' }),
  name: z.string().min(1).max(255).openapi({ example: 'IEP Cover Page' }),
  description: z.string().max(2000).openapi({ example: 'Standard IEP cover page with student info, meeting date, and team roster' }),
  documentType: z.enum([
    'iep', '504-plan', 'evaluation', 'consent', 'notice', 'transcript',
    'progress-report', 'referral', 'eligibility', 'manifestation', 'pwn',
    'iep-amendment', 'health-plan', 'mtss-plan', 'eval-report', 's504-evaluation',
  ]).openapi({ example: 'iep' }),
  locale: z.string().min(2).max(10).openapi({ example: 'en-US' }),
  engine: TemplateEngineSchema,
  content: z.string().min(1).openapi({ description: 'Raw template content or storage reference' }),
  variables: z.array(TemplateVariableSchema),
  conditionalSections: z.array(ConditionalSectionSchema).default([]),
  defaultLocale: z.string().min(2).max(10).openapi({ example: 'en-US' }),
  versions: z.record(z.string(), z.string()).openapi({
    description: 'Locale to template ID mapping for translated template variants',
    example: { es: 'tpl-1234-5679', vi: 'tpl-1234-5680' },
  }),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
}).openapi('Template', { description: 'A document template with variable bindings, conditional sections, and locale variants' })

export const TemplateRenderRequestSchema = z.object({
  templateId: z.string().uuid(),
  documentType: z.string().min(1),
  locale: z.string().min(2).max(10),
  variables: z.record(z.unknown()).openapi({ description: 'Variable values keyed by variable name' }),
  outputFormat: z.enum(['pdf', 'docx', 'html']).openapi({ example: 'pdf' }),
  includeConditionalSections: z.array(z.string()).nullable().openapi({ description: 'Force-include conditional sections by name' }),
  metadata: z.record(z.unknown()).nullable(),
}).openapi('TemplateRenderRequest', { description: 'A request to render a template with given variable bindings' })

export const TemplateRenderResponseSchema = z.object({
  versionId: z.string().uuid(),
  documentId: z.string().uuid(),
  storageLocation: z.string().min(1),
  hash: z.string().length(64),
  size: z.number().int().min(0),
  mimeType: z.string().openapi({ example: 'application/pdf' }),
  renderedAt: z.string().datetime(),
  pageCount: z.number().int().min(1).nullable(),
  warnings: z.array(z.string()).default([]),
}).openapi('TemplateRenderResponse', { description: 'The result of a template render operation' })

export const VariableBindingSchema = z.object({
  variableName: z.string().min(1).max(255),
  value: z.unknown(),
  source: z.string().nullable().openapi({ example: 'student.firstName', description: 'Domain property path' }),
  resolvedAt: z.string().datetime().nullable(),
}).openapi('VariableBinding', { description: 'A resolved variable binding with its source and value' })

export type TemplateEngine = z.infer<typeof TemplateEngineSchema>
export type TemplateVariableType = z.infer<typeof TemplateVariableTypeSchema>
export type TemplateVariable = z.infer<typeof TemplateVariableSchema>
export type ConditionalSection = z.infer<typeof ConditionalSectionSchema>
export type Template = z.infer<typeof TemplateSchema>
export type TemplateRenderRequest = z.infer<typeof TemplateRenderRequestSchema>
export type TemplateRenderResponse = z.infer<typeof TemplateRenderResponseSchema>
export type VariableBinding = z.infer<typeof VariableBindingSchema>
